import { Link } from "react-router-dom";
import { Crown, Radio } from "lucide-react";
import { useApi } from "../lib/api";
import { Brand } from "../components/Layout";
import { EmptyRow } from "../components/Common";
import { MapLegend, ZoneMap } from "../components/ZoneMap";
import { ChampionBanner, SeasonClock, SeasonHistory } from "../components/SeasonBits";

const PODIUM = ["border-amber-400/60 text-amber-300", "border-slate-400/50 text-slate-200", "border-orange-700/60 text-orange-300"];

const Podium = ({ rows }) => (
  <div className="grid gap-4 md:grid-cols-3">
    {rows.slice(0, 3).map((o, i) => (
      <div key={o.id} className={`panel fade-up border-t-2 p-6 ${PODIUM[i]}`} style={{ animationDelay: `${i * 80}ms` }} data-testid={`podium-${o.position}`}>
        <div className="flex items-center justify-between">
          <span className="font-mono text-4xl font-bold">#{o.position}</span>
          {i === 0 && <Crown className="h-6 w-6 text-amber-400" />}
        </div>
        <div className="mt-4 flex items-center gap-3 font-display text-2xl font-bold uppercase tracking-wider text-slate-50"><span className="h-3 w-3 rounded-full" style={{ background: o.color }} />{o.label}</div>
        <div className="mt-1 font-mono text-3xl font-bold text-emerald-400">{o.points}<span className="ml-1 text-sm text-slate-500">pts</span></div>
        <div className="mt-3 font-mono text-xs uppercase tracking-widest text-slate-500">{o.zones_held} zonas · {o.wins}V {o.losses}D · {o.kills} kills</div>
      </div>
    ))}
  </div>
);

export default function PublicRankingPage() {
  const { data, error } = useApi("/ranking", 30000);
  const { data: zones } = useApi("/zones", 10000);
  const { data: season } = useApi("/seasons/current", 30000);
  const { data: history } = useApi("/seasons", 60000);
  const rows = data || [];
  return (
    <div className="min-h-screen grid-bg">
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-emerald-500/20 bg-slate-950/90 px-8 py-5 backdrop-blur-lg">
        <Brand />
        <Link to="/login" className="font-mono text-xs uppercase tracking-widest text-slate-500 hover:text-emerald-300" data-testid="public-admin-link">Admin →</Link>
      </header>
      <main className="px-8 py-12 lg:px-16">
        <div className="fade-up mb-10 flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="eyebrow flex items-center gap-2"><Radio className="h-3.5 w-3.5 animate-pulse" />Classificação ao vivo</div>
            <h1 className="mt-2 text-4xl font-bold uppercase tracking-wider sm:text-5xl lg:text-6xl">Ranking das Organizações</h1>
            <p className="mt-3 max-w-xl text-slate-400">Pontos por vitórias em guerra, kills em combate e horas de domínio sobre as zonas de craft.</p>
          </div>
          <SeasonClock season={season} />
        </div>
        <ChampionBanner season={season} />
        {error && <div className="mb-6 text-rose-400" data-testid="public-ranking-error">{error}</div>}
        {rows.length > 0 && <Podium rows={rows} />}
        <div className="panel fade-up mt-8 overflow-x-auto">
          <table className="w-full text-left" data-testid="public-ranking-table">
            <thead className="border-b border-slate-800 font-mono text-[11px] uppercase tracking-widest text-slate-500">
              <tr><th className="px-4 py-3">#</th><th className="px-4 py-3">Organização</th><th className="px-4 py-3 text-right">Pontos</th><th className="px-4 py-3 text-right">Vitórias</th><th className="px-4 py-3 text-right">Derrotas</th><th className="px-4 py-3 text-right">Kills</th><th className="px-4 py-3 text-right">Zonas</th></tr>
            </thead>
            <tbody>
              {data && rows.length === 0 && <EmptyRow cols={7} text="Sem organizações." testid="public-ranking-empty" />}
              {rows.map((o) => (
                <tr key={o.id} className="row-hover border-b border-slate-800/70 hover:bg-slate-900/60" data-testid={`public-ranking-row-${o.name}`}>
                  <td className="px-4 py-3 font-mono font-bold text-slate-400">{o.position}</td>
                  <td className="px-4 py-3 font-medium"><span className="mr-2 inline-block h-2.5 w-2.5 rounded-full" style={{ background: o.color }} />{o.label}</td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-emerald-400" data-testid={`public-ranking-points-${o.name}`}>{o.points}</td>
                  <td className="px-4 py-3 text-right font-mono">{o.wins}</td>
                  <td className="px-4 py-3 text-right font-mono">{o.losses}</td>
                  <td className="px-4 py-3 text-right font-mono">{o.kills}</td>
                  <td className="px-4 py-3 text-right font-mono">{o.zones_held}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <section className="fade-up mt-14" data-testid="public-map-section">
          <div className="mb-4 flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="eyebrow">Mapa ao vivo</div>
              <h2 className="mt-1 text-2xl font-bold uppercase tracking-wider">Zonas de domínio</h2>
            </div>
            <MapLegend orgs={rows} />
          </div>
          <ZoneMap zones={zones} height={600} testid="public-zone-map" />
        </section>
        <section className="fade-up mt-14">
          <div className="eyebrow">Arquivo</div>
          <h2 className="mb-4 mt-1 text-2xl font-bold uppercase tracking-wider">Temporadas anteriores</h2>
          <SeasonHistory seasons={history} />
        </section>
      </main>
    </div>
  );
}
